import { useDataContext } from "client/context/DataContext";
import React, { useCallback, useMemo } from "react";
import { SFLProduct } from "types/sfl/interfaces";
import SectionTitle from "../section/section-title";
import ProductComponent, { isValidProduct } from "./product/product.component";
import {
  StyledGridRow,
  StyledProductsContainer,
  StyledProductsItem,
  StyledWrapper,
} from "./products.styles";
import { Product } from "types/searchApi/response";

export interface ProductsProps {
  isThreeColumn: boolean;
  brand?: string;
  isSFLVeryIEHeaderEnabled: boolean;
}

const PRODUCTS_LIMIT = 6;

const Products: React.FC<ProductsProps> = ({
  isThreeColumn,
  brand,
  isSFLVeryIEHeaderEnabled,
}) => {
  const { products, isRecommendations, recommendationsData } = useDataContext();

  const slicedProducts = useMemo(
    () => products?.filter(isValidProduct).slice(0, PRODUCTS_LIMIT) || [],
    [products],
  );

  const savedProducts = useMemo<Record<string, boolean>>(() => {
    try {
      if (typeof window === "undefined") return {};

      const SFLItems = sessionStorage.getItem("sfl_product_list");
      if (!SFLItems || !products) return {};

      const parsedData: { savedProducts?: SFLProduct[] } = JSON.parse(SFLItems);
      if (!Array.isArray(parsedData.savedProducts)) return {};

      return Object.fromEntries(
        parsedData.savedProducts.map((product) => [
          product.productId.toString(),
          true,
        ]),
      );
    } catch (error) {
      console.log(error);
      return {};
    }
  }, [products]);

  const recsPodId = recommendationsData?.popularProducts?.podId;
  const recsResultId = recommendationsData?.popularProducts?.resultId;
  const recsNumResults = recommendationsData?.popularProducts?.numResults;

  const cnstrcData = useMemo(() => {
    const data: Record<string, boolean | string | number> = {};
    if (isRecommendations) {
      data["data-cnstrc-recommendations"] = true;
      if (recsPodId) data["data-cnstrc-recommendations-pod-id"] = recsPodId;
      if (recsResultId) data["data-cnstrc-result-id"] = recsResultId;
      if (recsNumResults) data["data-cnstrc-num-results"] = recsNumResults;
    }
    return data;
  }, [isRecommendations, recsPodId, recsResultId, recsNumResults]);

  const getCnstrc: (product: Product) => Record<string, string | undefined> =
    useCallback(
      (product) => {
        return isRecommendations
          ? {
              ["data-cnstrc-item"]: "recommendation",
              ["data-cnstrc-strategy-id"]: product.meta.strategy.id,
            }
          : { ["data-cnstrc-item-section"]: "Products" };
      },
      [isRecommendations],
    );

  return (
    <StyledWrapper data-testid="products-wrapper" {...cnstrcData}>
      <SectionTitle dataTestId="products-wrapper-label">
        Suggested products
      </SectionTitle>
      <StyledProductsContainer data-testid="products-wrapper-list">
        <StyledGridRow aria-label="Suggested products" role="list">
          {slicedProducts.map((product) => (
            <StyledProductsItem
              key={product.product_id}
              data-testid="product-wrapper"
              data-cnstrc-item-id={product.product_id}
              data-cnstrc-item-name={product.title}
              xxs={12}
              l={isThreeColumn ? 4 : 6}
              className="grid-item"
              role="listitem"
              {...getCnstrc(product)}
            >
              <ProductComponent
                brand={brand as string}
                key={product.product_id}
                product={product}
                isSaved={!!savedProducts[product.product_id.toString()]}
                isAutosuggest={!isRecommendations}
                isSFLVeryIEHeaderEnabled={isSFLVeryIEHeaderEnabled}
              />
            </StyledProductsItem>
          ))}
        </StyledGridRow>
      </StyledProductsContainer>
    </StyledWrapper>
  );
};

export default Products;
